/**
 * Connector registry.
 *
 * Built-in connectors from `@skaile/workspaces/factory-assets` are registered here
 * via static ESM imports of their subpath exports so they are bundled into any
 * `bun --compile`'d agent binary AND any tsup-emitted ESM tarball. Each built-in
 * factory is wrapped in a `ConnectorTarget` and registered into the unified
 * `pluginRegistry` (`@skaile/workspaces/plugin-registry`). The public read
 * helpers (`getConnector` / `tryGetConnector` / `listConnectors`) are thin
 * kind-typed wrappers over that registry.
 *
 * The catalog path in `ConnectorManager.resolveConnector`
 * (`await import(connectorPath)` of raw TS) is a dev-only fallback for
 * project-local third-party connectors — it cannot work inside a compiled
 * binary because bun cannot share its bundled symbol graph with
 * externally-loaded TS files (the import silently hangs while trying to
 * resolve `@skaile/workspaces/connectors` against an absent `node_modules`).
 *
 * **Static import discipline.** Every subpath specifier below must be a
 * string-literal `from "@skaile/workspaces/factory-assets/connectors/<name>"` —
 * never aliased through a variable. Indirection defeats `bun --compile`'s
 * static analysis and silently drops the module from the binary.
 * Regression test: `tests/connector-tools-regression.test.ts`.
 */

import { createConnector as createDeployConnector } from "@skaile/workspaces/factory-assets/connectors/deploy";
// Service connectors (migrated in Task 9)
import { createConnector as createDevserverConnector } from "@skaile/workspaces/factory-assets/connectors/devserver";
// State connectors (migrated in Task 8)
import { createConnector as createFlowConnector } from "@skaile/workspaces/factory-assets/connectors/flow";

// Static subpath imports — string literals only.
// Filesystem-face connectors (mount drivers migrated in Task 6)
import { createConnector as createBoxConnector } from "@skaile/workspaces/factory-assets/connectors/box";
import { createConnector as createGitConnector } from "@skaile/workspaces/factory-assets/connectors/git";
import { createConnector as createGmailConnector } from "@skaile/workspaces/factory-assets/connectors/gmail";
import { createConnector as createGoogleDriveConnector } from "@skaile/workspaces/factory-assets/connectors/googledrive";
import { createConnector as createLocalConnector } from "@skaile/workspaces/factory-assets/connectors/local";
import { createConnector as createMattermostConnector } from "@skaile/workspaces/factory-assets/connectors/mattermost";
// Data connectors (migrated in Task 7)
import { createConnector as createMemoryConnector } from "@skaile/workspaces/factory-assets/connectors/memory";
import { createConnector as createMinioConnector } from "@skaile/workspaces/factory-assets/connectors/minio";
import { createConnector as createPostgresConnector } from "@skaile/workspaces/factory-assets/connectors/postgres";
import { createConnector as createS3Connector } from "@skaile/workspaces/factory-assets/connectors/s3";
import { createConnector as createSharepointConnector } from "@skaile/workspaces/factory-assets/connectors/sharepoint";
import { createConnector as createSqliteConnector } from "@skaile/workspaces/factory-assets/connectors/sqlite";
// Subpath and registry key (CONNECTOR.md `name:`) are both `static-server`.
import { createConnector as createStaticConnector } from "@skaile/workspaces/factory-assets/connectors/static-server";
import { createConnector as createTunnelConnector } from "@skaile/workspaces/factory-assets/connectors/tunnel";
import { createConnector as createWebdavConnector } from "@skaile/workspaces/factory-assets/connectors/webdav";
import { createConnector as createXstateConnector } from "@skaile/workspaces/factory-assets/connectors/xstate";
import { createConnector as createXstateStoreConnector } from "@skaile/workspaces/factory-assets/connectors/xstate-store";
import {
  type ConnectorTarget,
  type PluginRegistry,
  pluginRegistry,
} from "@skaile/workspaces/plugin-registry";
import * as z from "zod";
import type { Connector, ConnectorFactory } from "./connector-types.js";

// ── Unified Connector registry ───────────────────────────────────────────────
// Built-ins are stored in the shared pluginRegistry under the "connector" kind.

/**
 * Static metadata for every built-in connector — pure data, no imports.
 *
 * Mirrors the ids registered by {@link registerBuiltinConnectors}; selection UIs
 * use it to enumerate the built-in set without loading any connector module
 * (parity with `BUILTIN_DRIVER_CATALOG`).
 *
 * @docLink packages/connectors/api-reference#builtin-connector-catalog
 */
export const BUILTIN_CONNECTOR_CATALOG: readonly { id: string; displayName: string }[] = [
  { id: "git", displayName: "git" },
  { id: "local", displayName: "local" },
  { id: "s3", displayName: "s3" },
  { id: "webdav", displayName: "webdav" },
  { id: "sharepoint", displayName: "sharepoint" },
  { id: "googledrive", displayName: "googledrive" },
  { id: "box", displayName: "box" },
  { id: "memory", displayName: "memory" },
  { id: "postgres", displayName: "postgres" },
  { id: "sqlite", displayName: "sqlite" },
  { id: "minio", displayName: "minio" },
  { id: "flow", displayName: "flow" },
  { id: "xstate", displayName: "xstate" },
  { id: "xstate-store", displayName: "xstate-store" },
  { id: "devserver", displayName: "devserver" },
  { id: "static-server", displayName: "static-server" },
  { id: "tunnel", displayName: "tunnel" },
  { id: "deploy", displayName: "deploy" },
  { id: "gmail", displayName: "gmail" },
  { id: "mattermost", displayName: "mattermost" },
];

/**
 * Build a `ConnectorTarget` wrapping an existing connector factory.
 *
 * Connectors take no construct-time config, so `configSchema` is a passthrough
 * and `create()` simply calls the factory. Lifecycle config arrives later via
 * `Connector.connect(declaration, ctx)`.
 */
function connectorTargetFromFactory(id: string, factory: ConnectorFactory): ConnectorTarget {
  return {
    id,
    displayName: id,
    apiVersion: 1,
    configSchema: z.unknown(),
    create: (_config, _ctx) => factory(),
  };
}

let _connectorsRegistered = false;

/**
 * Register every built-in connector into the plugin registry.
 *
 * **Static import discipline.** Every subpath specifier in this file must be a
 * string-literal `from "@skaile/workspaces/factory-assets/connectors/<name>"` —
 * never aliased through a variable. Indirection defeats `bun --compile`'s
 * static analysis and silently drops the module from the binary.
 *
 * Idempotent: guarded by a module flag so a second call is a no-op (a duplicate
 * `register` would otherwise throw `RegistrationError`).
 *
 * @docLink packages/connectors/api-reference#register-builtin-connectors
 */
export function registerBuiltinConnectors(registry: PluginRegistry = pluginRegistry): void {
  if (registry === pluginRegistry && _connectorsRegistered) return;

  const reg = (id: string, factory: ConnectorFactory): void => {
    if (registry.get("connector", id)) return;
    registry.register("connector", connectorTargetFromFactory(id, factory));
  };

  // Filesystem-face connectors (former mount drivers, migrated in Task 6)
  reg("git", () => createGitConnector());
  reg("local", () => createLocalConnector());
  reg("s3", () => createS3Connector());
  reg("webdav", () => createWebdavConnector());
  reg("sharepoint", () => createSharepointConnector());
  reg("googledrive", () => createGoogleDriveConnector());
  reg("box", () => createBoxConnector());

  // Data connectors (migrated in Task 7)
  reg("memory", () => createMemoryConnector());
  reg("postgres", () => createPostgresConnector());
  reg("sqlite", () => createSqliteConnector());
  reg("minio", () => createMinioConnector());

  // State connectors (migrated in Task 8)
  reg("flow", () => createFlowConnector());
  reg("xstate", () => createXstateConnector());
  reg("xstate-store", () => createXstateStoreConnector());

  // Service connectors (migrated in Task 9)
  reg("devserver", () => createDevserverConnector());
  // Subpath and registry key (CONNECTOR.md `name:`) are both `static-server`.
  reg("static-server", () => createStaticConnector());
  reg("tunnel", () => createTunnelConnector());
  reg("deploy", () => createDeployConnector());

  // Integration connectors (migrated in Task 10)
  reg("gmail", () => createGmailConnector());
  reg("mattermost", () => createMattermostConnector());

  if (registry === pluginRegistry) _connectorsRegistered = true;
}

/**
 * Get a connector by name, calling its target's factory to produce a fresh
 * instance. Ensures the built-ins are registered first (cheap, idempotent).
 * @param name - Connector key (e.g. `"postgres"`).
 * @returns A fresh `Connector` instance.
 * @throws {Error} when no connector is registered under `name`.
 * @docLink packages/connectors/api-reference#get-connector
 */
export function getConnector(name: string): Connector {
  ensureBuiltinsRegistered();
  const target = pluginRegistry.get("connector", name);
  if (!target) {
    const available = pluginRegistry
      .list("connector")
      .map((m) => m.id)
      .join(", ");
    throw new Error(`Unknown connector: "${name}". Available: ${available || "none"}`);
  }
  return target.create({}, {});
}

/**
 * Get a connector by name, or return `undefined` if not registered. Ensures the
 * built-ins are registered first (cheap, idempotent).
 * @param name - Connector key.
 * @returns A fresh `Connector` instance or `undefined`.
 * @docLink packages/connectors/api-reference#try-get-connector
 */
export function tryGetConnector(name: string): Connector | undefined {
  ensureBuiltinsRegistered();
  return pluginRegistry.get("connector", name)?.create({}, {});
}

/**
 * Return the names of all currently registered connectors. Ensures the built-ins
 * are registered first (cheap, idempotent).
 * @returns Array of connector keys.
 * @docLink packages/connectors/api-reference#list-connectors
 */
export function listConnectors(): string[] {
  ensureBuiltinsRegistered();
  return pluginRegistry.list("connector").map((m) => m.id);
}

/**
 * Lazily register the built-ins unless they have already been registered.
 *
 * Production registers them eagerly at startup (`serve.ts`, connector-manager);
 * this is the cheap safety net for callers / tests that look a connector up
 * before that startup hook runs. Gating on the `_connectorsRegistered` flag
 * (not registry emptiness) keeps built-ins available even when a third-party
 * connector plugin registered first — an empty-check would skip the built-ins
 * entirely and make `getConnector("git")` fail. `registerBuiltinConnectors`
 * skips ids already present, so a pre-seeded fake is never clobbered.
 */
function ensureBuiltinsRegistered(): void {
  if (!_connectorsRegistered) {
    registerBuiltinConnectors();
  }
}
